/**
 * The Confirm/Cancel control for a tool call ADK has paused.
 *
 * This is the whole point of moving to ADK's native confirmation: the decision
 * becomes a button the researcher presses, not a sentence the model has to
 * interpret. Pressing Confirm sends a FunctionResponse that ADK pairs with the
 * paused call and resumes — the model is never asked to re-issue anything, so
 * the "are you sure? … are you sure? … are you sure?" loop cannot happen.
 *
 * Deliberately not a modal. The prose above it is the preview — the assistant
 * has just listed the notes it intends to merge, with links — and a modal
 * would cover exactly the thing the researcher needs to read before deciding.
 */

import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { useEffect } from 'react';

import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import type { PendingToolConfirmation } from '../../../services/agentChat.service';
import { safeAnalytics } from '../../../utils/analytics';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { confirmationCopy } from './toolConfirmationCopy';

interface ToolConfirmationPromptProps {
  readonly confirmation: PendingToolConfirmation;
  readonly onRespond: (confirmed: boolean) => void;
  /** True while a reply is in flight — both buttons lock to prevent a double send. */
  readonly isResponding?: boolean;
}

export function ToolConfirmationPrompt({
  confirmation,
  onRespond,
  isResponding = false,
}: ToolConfirmationPromptProps) {
  const copy = confirmationCopy(confirmation.tool, confirmation.args, confirmation.hint);

  // Fired when the prompt RENDERS, which is the only moment that distinguishes
  // "the researcher was shown a control" from "the agent asked for an approval
  // nobody could give" — the #1619 state, which emitted nothing at all because
  // only the ANSWERED event existed.
  //
  // Carries the tool name and destructiveness only. `confirmation.args` is
  // entity UUIDs and, for create_visual_reference_set, researcher-authored
  // category text, so it stays out of analytics (rules/analytics.md: IDs only,
  // and UGC never without an egress decision).
  //
  // Every dep is a primitive derived from one paused call, and the parent keys
  // this component by `confirmation.id`, so the effect runs once per prompt
  // without needing an exhaustive-deps escape hatch.
  //
  // `replayed` keeps the event interpretable now that a prompt survives a
  // reload: an unanswered one is re-displayed every time the panel reopens, so
  // without it the displayed→answered rate quietly becomes a measure of how
  // often researchers reopen Bird.
  const { id: confirmationId, tool, replayed } = confirmation;
  const { destructive } = copy;
  useEffect(() => {
    safeAnalytics.track(ANALYTICS_EVENTS.CHAT_TOOL_CONFIRMATION_DISPLAYED, {
      tool,
      destructive,
      replayed: replayed === true,
    });
  }, [confirmationId, tool, destructive, replayed]);

  return (
    <Box
      mt={3}
      p={3}
      borderWidth="1px"
      borderColor={copy.destructive ? 'border.error' : 'border.default'}
      borderRadius="md"
      bg="bg.subtle"
      // `role="group"` alone announces NOTHING when this appears mid-stream —
      // it is not a live region, so a screen-reader user is left waiting on a
      // stream that has silently stopped for input. `aria-live="polite"` is
      // what actually speaks it, once, after the current utterance.
      role="group"
      aria-live="polite"
      aria-label={copy.title}
    >
      <VStack align="stretch" spacing={2}>
        <HStack spacing={2} align="start">
          <MaterialIcon
            name={copy.destructive ? 'warning' : 'help'}
            boxSize={4}
            color={copy.destructive ? 'text.error' : 'text.muted'}
            aria-hidden
          />
          <VStack align="start" spacing={0.5} minW={0}>
            <Text fontSize="sm" fontWeight="semibold" color="text.primary">
              {copy.title}
            </Text>
            <Text fontSize="xs" color="text.muted" overflowWrap="anywhere">
              {copy.detail}
            </Text>
          </VStack>
        </HStack>
        <HStack spacing={2} justify="flex-end">
          <Button
            size="xs"
            variant="ghost"
            onClick={() => onRespond(false)}
            isDisabled={isResponding}
          >
            Cancel
          </Button>
          <Button
            size="xs"
            // A VARIANT, not a colorScheme. This read `colorScheme={destructive
            // ? 'red' : 'accent'}` and the confirm button for a destructive
            // tool was never red: every Button variant is declared in
            // `theme/components.button.ts`, and a declared object wins the
            // `extendTheme` merge before Chakra's colorScheme machinery — the
            // only thing `colorScheme` feeds — ever runs. The box around it was
            // right all along (`border.error`, the warning glyph); only the
            // button, the one control that actually does the destructive
            // thing, rendered as an ordinary brand CTA.
            variant={copy.destructive ? 'danger' : 'primary'}
            onClick={() => onRespond(true)}
            isLoading={isResponding}
            loadingText="Working…"
          >
            {copy.confirmLabel}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
